import { z } from 'zod';

import {
  PLAYER_ROLES,
  playerRoleSchema,
  type PlayerRoleCounts,
  type PlayerRoleValue,
} from './player.ts';

/**
 * Contratto REST delle aste: creazione, lettura, configurazione, eliminazione.
 *
 * Gli enum sono ridichiarati qui e non importati da `@falasta/database`: questa
 * libreria finisce anche nel bundle del browser e non deve conoscere Prisma. La
 * corrispondenza con gli enum del database è verificata da un test dedicato in
 * `libs/database`, così una divergenza rompe la build invece di arrivare in
 * produzione.
 */
export const AUCTION_ROUTES = {
  collection: '/auctions',
  /** Template Fastify; per costruire un URL concreto usa `auctionPath()`. */
  byId: '/auctions/:id',
} as const;

export function auctionPath(auctionId: string): string {
  return `${AUCTION_ROUTES.collection}/${encodeURIComponent(auctionId)}`;
}

// ---------------------------------------------------------------------------
// Enum condivisi
// ---------------------------------------------------------------------------

export const AUCTION_STATUSES = [
  'DRAFT',
  'READY',
  'RUNNING',
  'PAUSED',
  'STOPPED',
  'FINISHED',
] as const;

export type AuctionStatusValue = (typeof AUCTION_STATUSES)[number];

export const auctionStatusSchema = z.enum(AUCTION_STATUSES);

/**
 * Modalità di chiamata.
 *
 * `ROLE_BASED` — l'asta procede reparto per reparto, nell'ordine P → D → C → A
 * a rotazione dal reparto scelto; dentro ogni reparto l'ordine è alfabetico
 * dalla lettera configurata, oppure casuale se quel reparto è su
 * `START_LETTER_RANDOM`.
 *
 * `RANDOM` — tutti i calciatori mescolati insieme, **senza considerare i
 * reparti**: un portiere può essere chiamato dopo un attaccante. Le lettere di
 * partenza non hanno significato in questa modalità.
 */
export const PLAYER_CALL_MODES = ['ROLE_BASED', 'RANDOM'] as const;

export type PlayerCallModeValue = (typeof PLAYER_CALL_MODES)[number];

export const playerCallModeSchema = z.enum(PLAYER_CALL_MODES);

/** Etichette mostrate all'amministratore: la UI non espone mai i valori grezzi. */
export const PLAYER_CALL_MODE_LABELS: Readonly<Record<PlayerCallModeValue, string>> = {
  ROLE_BASED: 'Per ruolo',
  RANDOM: 'Casuale',
};

/** Lettere ammesse come punto di partenza dell'ordine alfabetico. */
export const ALPHABET_LETTERS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
] as const;

export type AlphabetLetter = (typeof ALPHABET_LETTERS)[number];

export const alphabetLetterSchema = z.enum(ALPHABET_LETTERS);

/**
 * Il valore che, al posto di una lettera, rende **casuale** l'ordine di un
 * reparto in modalità `ROLE_BASED`. Non è una lettera di proposito: convive
 * con A–Z nello stesso campo, ma nessun nome può cominciare per `RANDOM`.
 */
export const START_LETTER_RANDOM = 'RANDOM' as const;

export const START_LETTER_CHOICES = [...ALPHABET_LETTERS, START_LETTER_RANDOM] as const;

export type StartLetterChoice = (typeof START_LETTER_CHOICES)[number];

export const startLetterChoiceSchema = z.enum(START_LETTER_CHOICES);

/**
 * Punto di partenza per ciascun reparto: una lettera, oppure
 * `START_LETTER_RANDOM` per un ordine casuale dentro quel reparto.
 * Le chiavi ci sono sempre tutte.
 */
export type RoleStartLetters = Readonly<Record<PlayerRoleValue, StartLetterChoice>>;

/** `true` se almeno un reparto è configurato con ordine casuale. */
export function hasRandomStartLetter(letters: RoleStartLetters): boolean {
  return Object.values(letters).includes(START_LETTER_RANDOM);
}

// ---------------------------------------------------------------------------
// Configurazione
// ---------------------------------------------------------------------------

/**
 * Configurazione di una singola asta.
 *
 * Sono i quattro parametri che l'amministratore controlla. `initialPrice` e
 * `allowedIncrements` esistono nel modello dati ma restano gestiti dal server:
 * in questa versione il prezzo di partenza è 0 e gli incrementi sono 1/3/5/20.
 */
export interface AuctionSettingsPayload {
  /** Attesa fra la conclusione di una chiamata e l'apertura della successiva. */
  readonly callIntervalSeconds: number;
  /** Inattività dopo l'ultima offerta valida prima dell'aggiudicazione. */
  readonly bidTimeoutSeconds: number;
  /** Fantamilioni assegnati di default a ogni fantallenatore. */
  readonly initialBudget: number;
  readonly playerCallMode: PlayerCallModeValue;
  /**
   * Slot per ruolo: quanti calciatori di ciascun ruolo un fantallenatore può
   * aggiudicarsi. Esauriti gli slot di un ruolo, le sue offerte su quel ruolo
   * vengono rifiutate — la decisione autorevole è del processor.
   */
  readonly rosterLimits: PlayerRoleCounts;
  /**
   * Reparto da cui parte l'asta. Gli altri seguono **a rotazione** nell'ordine
   * P, D, C, A: partendo da `C` si chiamano centrocampisti, attaccanti,
   * portieri e infine difensori. È una rotazione, non un filtro — nessun
   * reparto resta fuori.
   *
   * Vale per entrambe le modalità: riguarda l'ordine dei reparti, non quello
   * dentro il reparto.
   */
  readonly startRole: PlayerRoleValue;
  /**
   * Punto di partenza dell'ordine, **uno per reparto**: una lettera oppure
   * `START_LETTER_RANDOM`. Da `M` si chiamano prima i nomi da M a Z e poi
   * quelli da A a L: è una rotazione, nessun calciatore viene escluso. Con
   * `RANDOM` (il valore, non la modalità) l'ordine di quel reparto lo decide
   * il seme.
   *
   * Ignorate dalla modalità `RANDOM`, dove i reparti non contano affatto.
   */
  readonly startLetters: RoleStartLetters;
}

/**
 * Limiti di validazione.
 *
 * Esposti perché il form del browser mostri gli stessi vincoli che il server
 * applica: la validazione **autorevole** resta comunque quella del server.
 */
export const AUCTION_SETTINGS_LIMITS = {
  callIntervalSeconds: { min: 0, max: 300 },
  bidTimeoutSeconds: { min: 1, max: 120 },
  initialBudget: { min: 1, max: 100_000 },
  /**
   * Slot di un singolo ruolo. Lo zero è ammesso di proposito: un'asta può
   * escludere un ruolo (nessuno può comprare portieri) e allora quel ruolo
   * resterà semplicemente invenduto.
   */
  rosterLimitPerRole: { min: 0, max: 50 },
} as const;

export const AUCTION_NAME_MIN_LENGTH = 3;
export const AUCTION_NAME_MAX_LENGTH = 120;

/** Numero massimo di aste restituite da una singola pagina della lista. */
export const AUCTION_LIST_MAX_TAKE = 100;
export const AUCTION_LIST_DEFAULT_TAKE = 50;

/**
 * Rosa standard del Fantacalcio: 3 portieri, 8 difensori, 8 centrocampisti,
 * 6 attaccanti. È il default, non un vincolo: ogni asta può cambiarlo prima
 * dell'avvio.
 */
export const DEFAULT_ROSTER_LIMITS: PlayerRoleCounts = { P: 3, D: 8, C: 8, A: 6 };

/** Ogni reparto comincia dalla A finché l'amministratore non decide altrimenti. */
export const DEFAULT_START_LETTERS: RoleStartLetters = { P: 'A', D: 'A', C: 'A', A: 'A' };

/** Quanti calciatori compone una rosa completa con questi limiti. */
export function rosterSlotsTotal(limits: PlayerRoleCounts): number {
  return PLAYER_ROLES.reduce((total, role) => total + limits[role], 0);
}

/**
 * Default di una nuova asta. Unica fonte di verità: le rotte li applicano
 * esplicitamente, i default del database sono solo una rete di sicurezza (un
 * test di integrazione verifica che i due insiemi coincidano).
 */
export const DEFAULT_AUCTION_SETTINGS: AuctionSettingsPayload = {
  callIntervalSeconds: 5,
  /** Default richiesto dal capitolato. */
  bidTimeoutSeconds: 3,
  initialBudget: 500,
  playerCallMode: 'ROLE_BASED',
  rosterLimits: DEFAULT_ROSTER_LIMITS,
  /** I default esistono per non rompere una creazione minimale: la scelta vera
   * — reparto e lettere — la fa l'amministratore prima di generare la sequenza. */
  startRole: 'P',
  startLetters: DEFAULT_START_LETTERS,
};

/**
 * I due tempi del motore, esportati perché non li usa soltanto il REST: sono
 * anche il payload del comando che li cambia a asta in pausa
 * (`UPDATE_TIMING`), e i limiti devono essere gli stessi su tutte le porte.
 */
export const callIntervalSecondsSchema = z
  .number()
  .int('Deve essere un numero intero di secondi')
  .min(AUCTION_SETTINGS_LIMITS.callIntervalSeconds.min, 'Non può essere negativo')
  .max(
    AUCTION_SETTINGS_LIMITS.callIntervalSeconds.max,
    `Non può superare ${AUCTION_SETTINGS_LIMITS.callIntervalSeconds.max} secondi`,
  );

export const bidTimeoutSecondsSchema = z
  .number()
  .int('Deve essere un numero intero di secondi')
  .min(AUCTION_SETTINGS_LIMITS.bidTimeoutSeconds.min, 'Deve essere almeno 1 secondo')
  .max(
    AUCTION_SETTINGS_LIMITS.bidTimeoutSeconds.max,
    `Non può superare ${AUCTION_SETTINGS_LIMITS.bidTimeoutSeconds.max} secondi`,
  );

const initialBudgetSchema = z
  .number()
  .int('Deve essere un numero intero di fantamilioni')
  .min(AUCTION_SETTINGS_LIMITS.initialBudget.min, 'Il budget deve essere positivo')
  .max(
    AUCTION_SETTINGS_LIMITS.initialBudget.max,
    `Non può superare ${AUCTION_SETTINGS_LIMITS.initialBudget.max} fantamilioni`,
  );

const rosterLimitSchema = z
  .number()
  .int('Deve essere un numero intero di slot')
  .min(AUCTION_SETTINGS_LIMITS.rosterLimitPerRole.min, 'Non può essere negativo')
  .max(
    AUCTION_SETTINGS_LIMITS.rosterLimitPerRole.max,
    `Non può superare ${AUCTION_SETTINGS_LIMITS.rosterLimitPerRole.max} slot per ruolo`,
  );

/**
 * Slot per ruolo, **tutti e quattro insieme**.
 *
 * Niente campi opzionali di proposito: una patch parziale come `{ P: 4 }`
 * dovrebbe inventare gli altri tre, e inventarli significherebbe riportare in
 * silenzio difensori, centrocampisti e attaccanti ai valori di default. Meglio
 * un 400 esplicito: il client rimanda l'oggetto completo che ha appena letto.
 */
export const rosterLimitsSchema = z.object({
  P: rosterLimitSchema,
  D: rosterLimitSchema,
  C: rosterLimitSchema,
  A: rosterLimitSchema,
});

/**
 * Lettere di partenza, **tutte e quattro insieme**.
 *
 * Come per gli slot: una patch parziale dovrebbe inventare i reparti mancanti,
 * e inventarli significherebbe riportarli in silenzio alla A. Meglio un 400.
 */
export const startLettersSchema = z.object({
  P: startLetterChoiceSchema,
  D: startLetterChoiceSchema,
  C: startLetterChoiceSchema,
  A: startLetterChoiceSchema,
});

export const auctionNameSchema = z
  .string()
  .trim()
  .min(AUCTION_NAME_MIN_LENGTH, 'Il nome dell’asta è troppo corto')
  .max(AUCTION_NAME_MAX_LENGTH, `Il nome non può superare ${AUCTION_NAME_MAX_LENGTH} caratteri`);

/** Configurazione completa, con i default applicati campo per campo. */
export const auctionSettingsInputSchema = z.object({
  callIntervalSeconds: callIntervalSecondsSchema.default(
    DEFAULT_AUCTION_SETTINGS.callIntervalSeconds,
  ),
  bidTimeoutSeconds: bidTimeoutSecondsSchema.default(DEFAULT_AUCTION_SETTINGS.bidTimeoutSeconds),
  initialBudget: initialBudgetSchema.default(DEFAULT_AUCTION_SETTINGS.initialBudget),
  playerCallMode: playerCallModeSchema.default(DEFAULT_AUCTION_SETTINGS.playerCallMode),
  rosterLimits: rosterLimitsSchema.default(DEFAULT_AUCTION_SETTINGS.rosterLimits),
  startRole: playerRoleSchema.default(DEFAULT_AUCTION_SETTINGS.startRole),
  startLetters: startLettersSchema.default(DEFAULT_AUCTION_SETTINGS.startLetters),
});

/** Configurazione parziale: in PATCH si modifica solo ciò che si invia. */
export const auctionSettingsPatchSchema = z.object({
  callIntervalSeconds: callIntervalSecondsSchema.optional(),
  bidTimeoutSeconds: bidTimeoutSecondsSchema.optional(),
  initialBudget: initialBudgetSchema.optional(),
  playerCallMode: playerCallModeSchema.optional(),
  rosterLimits: rosterLimitsSchema.optional(),
  startRole: playerRoleSchema.optional(),
  startLetters: startLettersSchema.optional(),
});

export type AuctionSettingsPatch = z.output<typeof auctionSettingsPatchSchema>;

// ---------------------------------------------------------------------------
// Richieste
// ---------------------------------------------------------------------------

export const createAuctionRequestSchema = z.object({
  name: auctionNameSchema,
  settings: auctionSettingsInputSchema.default(DEFAULT_AUCTION_SETTINGS),
});

export type CreateAuctionRequest = z.input<typeof createAuctionRequestSchema>;

/**
 * Aggiornamento: almeno un campo deve cambiare.
 *
 * Un PATCH vuoto sarebbe una scrittura (e un evento di audit) senza contenuto:
 * è un errore del chiamante, non un no-op silenzioso.
 */
export const updateAuctionRequestSchema = z
  .object({
    name: auctionNameSchema.optional(),
    settings: auctionSettingsPatchSchema.optional(),
  })
  .refine(
    (value) =>
      value.name !== undefined ||
      (value.settings !== undefined && Object.keys(value.settings).length > 0),
    { message: 'Nessuna modifica richiesta' },
  );

export type UpdateAuctionRequest = z.input<typeof updateAuctionRequestSchema>;

export const auctionIdParamsSchema = z.object({
  id: z.uuid('Identificativo asta non valido'),
});

export const listAuctionsQuerySchema = z.object({
  status: auctionStatusSchema.optional(),
  take: z.coerce
    .number()
    .int()
    .min(1)
    .max(AUCTION_LIST_MAX_TAKE)
    .default(AUCTION_LIST_DEFAULT_TAKE),
  skip: z.coerce.number().int().min(0).default(0),
});

export type ListAuctionsQuery = z.input<typeof listAuctionsQuerySchema>;

// ---------------------------------------------------------------------------
// Risposte
// ---------------------------------------------------------------------------

/** Quanto è stata popolata l'asta: serve alla dashboard per capire cosa manca. */
export interface AuctionCountsPayload {
  readonly players: number;
  readonly participants: number;
}

export interface AuctionSummary {
  readonly id: string;
  readonly name: string;
  readonly status: AuctionStatusValue;
  /** ISO-8601. */
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly counts: AuctionCountsPayload;
}

export interface AuctionDetail extends AuctionSummary {
  readonly settings: AuctionSettingsPayload;
}

export interface AuctionResponse {
  readonly auction: AuctionDetail;
}

export interface AuctionListResponse {
  readonly auctions: readonly AuctionSummary[];
  /** Totale delle aste che soddisfano il filtro, indipendente dalla pagina. */
  readonly total: number;
}

export interface DeleteAuctionResponse {
  readonly deleted: true;
}

// ---------------------------------------------------------------------------
// Regole di modifica
// ---------------------------------------------------------------------------

/**
 * Stati in cui la configurazione è ancora modificabile.
 *
 * Dopo l'avvio, budget, tempi e modalità di chiamata sono congelati: la
 * sequenza è già stata generata e i budget sono già in gioco.
 */
export const AUCTION_SETTINGS_EDITABLE_STATUSES: readonly AuctionStatusValue[] = ['DRAFT', 'READY'];

/**
 * Stati in cui i **tempi** si possono ancora cambiare a asta avviata.
 *
 * Sono l'eccezione motivata alla regola qui sopra, e vale solo per i due
 * tempi del motore: durata del countdown e intervallo fra le chiamate. Non
 * riscrivono niente di già deciso — nessun budget, nessuna sequenza, nessuna
 * aggiudicazione — mentre budget, slot e modalità di chiamata restano
 * congelati per le ragioni di sempre.
 *
 * Solo in `PAUSED`: a chiamata aperta un cambio di countdown sposterebbe una
 * scadenza che i fantallenatori stanno guardando scorrere, e il rilancio
 * partito un istante prima verrebbe misurato con un metro diverso da quello
 * mostrato. La modifica passa dal processor (`UPDATE_TIMING`), non dal REST:
 * a asta viva l'unica autorità sui tempi è il motore.
 */
export const AUCTION_TIMING_EDITABLE_STATUSES: readonly AuctionStatusValue[] = ['PAUSED'];

/**
 * Stati in cui la rosa dei calciatori è ancora sostituibile.
 *
 * Coincidono con quelli della configurazione e per la stessa ragione: dopo
 * l'avvio esiste una sequenza di chiamata, e possono esistere acquisti che
 * puntano a quei calciatori. Reimportare il file cancellerebbe le righe a cui
 * lo storico fa riferimento.
 */
export const AUCTION_PLAYERS_EDITABLE_STATUSES: readonly AuctionStatusValue[] = ['DRAFT', 'READY'];

/**
 * Stati in cui l'elenco dei partecipanti è modificabile.
 *
 * Aggiungere, rimuovere o cambiare il budget di un fantallenatore dopo l'avvio
 * altererebbe un'asta in corso. **Revocare e rinviare un invito resta invece
 * sempre possibile**: riguarda l'accesso, non i parametri di gioco, e a metà
 * asta è esattamente quando serve (link smarrito, casella sbagliata).
 */
export const AUCTION_PARTICIPANTS_EDITABLE_STATUSES: readonly AuctionStatusValue[] = [
  'DRAFT',
  'READY',
];

/**
 * Stati in cui la sequenza di chiamata è ancora (ri)generabile.
 *
 * Dopo l'avvio l'ordine è congelato: rigenerarlo a asta in corso
 * rimescolerebbe calciatori già venduti e renderebbe insensata la progressione
 * mostrata a tutti i client.
 */
export const AUCTION_SEQUENCE_EDITABLE_STATUSES: readonly AuctionStatusValue[] = ['DRAFT', 'READY'];

/** Un'asta viva non si cancella: prima va fermata. */
export const AUCTION_DELETABLE_STATUSES: readonly AuctionStatusValue[] = [
  'DRAFT',
  'READY',
  'STOPPED',
  'FINISHED',
];
